/**
 * Centralized logging utility with environment-aware filtering
 * Replaces console.log/error/warn throughout the application
 * 
 * Features:
 * - Production log filtering (only errors/warnings)
 * - Structured logging with context
 * - Performance monitoring
 * - Easy to integrate with external services (Sentry, LogRocket, etc.)
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  sessionId?: string;
  [key: string]: unknown;
}

/**
 * Supabase-style error fields that we surface to Sentry as extras.
 * Mirrors PostgrestError shape without depending on the Supabase types here.
 */
export interface SupabaseErrorExtras {
  code?: unknown;
  details?: unknown;
  hint?: unknown;
  status?: unknown;
  statusCode?: unknown;
}

export type NormalizedError = Error & SupabaseErrorExtras;

/**
 * Minimal shape of the optional Sentry global the logger talks to.
 * Sentry is loaded via a script tag in production, so we feature-detect on window.
 */
interface SentryLike {
  captureException: (error: unknown, options?: Record<string, unknown>) => void;
  captureMessage: (message: string, options?: Record<string, unknown>) => void;
}

/** Subset of gtag we use — Google Analytics 4 event dispatch. */
type GtagFn = (command: 'event', name: string, params: Record<string, unknown>) => void;

/** Subset of the legacy `window.firebase` namespace we feature-detect. */
interface FirebaseLike {
  analytics?: () => { logEvent: (name: string, params: Record<string, unknown>) => void };
}

/** `performance.memory` is non-standard but present in Chromium browsers. */
interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface WindowWithLoggerGlobals extends Window {
  Sentry?: SentryLike;
  gtag?: GtagFn;
  firebase?: FirebaseLike;
  performance: Performance & { memory?: PerformanceMemory };
}

/**
 * Normalize anything thrown/returned as an error into a real Error instance.
 *
 * Supabase returns `{ code, details, hint, message }` plain objects — passing those
 * directly to Sentry.captureException produces the unhelpful
 * "Object captured as exception with keys: code, details, hint, message" issue
 * (Sentry POKERBLITZ-6). This wraps non-Error inputs so Sentry can group them
 * properly and the fingerprint/stack survive.
 */
export function normalizeError(input: unknown): NormalizedError {
  if (input instanceof Error) return input as NormalizedError;
  if (input == null) return new Error('Unknown error (null/undefined)') as NormalizedError;
  if (typeof input === 'string') return new Error(input) as NormalizedError;

  if (typeof input === 'object') {
    const obj = input as Record<string, unknown>;
    const message =
      (typeof obj.message === 'string' && obj.message) ||
      (typeof obj.error_description === 'string' && obj.error_description) ||
      (typeof obj.error === 'string' && obj.error) ||
      JSON.stringify(obj);

    const err = new Error(String(message)) as NormalizedError;
    // Preserve Supabase-style fields so Sentry "extra" stays useful.
    if (typeof obj.code !== 'undefined') err.code = obj.code;
    if (typeof obj.details !== 'undefined') err.details = obj.details;
    if (typeof obj.hint !== 'undefined') err.hint = obj.hint;
    if (typeof obj.status !== 'undefined') err.status = obj.status;
    if (typeof obj.statusCode !== 'undefined') err.statusCode = obj.statusCode;
    return err;
  }

  return new Error(String(input)) as NormalizedError;
}

class Logger {
  private isDevelopment: boolean;
  private isTest: boolean;
  private sessionId: string;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.isTest = import.meta.env.MODE === 'test';
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private shouldLog(level: LogLevel): boolean {
    // In test mode, suppress all logs unless explicitly needed
    if (this.isTest) return false;

    // In production, only log warnings and errors
    if (!this.isDevelopment) {
      return level === 'warn' || level === 'error';
    }

    // In development, log everything
    return true;
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` ${JSON.stringify(context)}` : '';
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`;
  }

  /**
   * Debug-level logging (only in development)
   * Use for detailed debugging information
   *
   * Arrow field so detached calls (`const f = logger.debug; f()`) keep a valid
   * `this` — prevents Sentry POKERBLITZ-E (`shouldLog` of undefined).
   */
  debug = (message: string, context?: LogContext): void => {
    if (!this.shouldLog('debug')) return;

    const formatted = this.formatMessage('debug', message, {
      ...context,
      sessionId: this.sessionId,
    });
    console.log(formatted);
  };

  /**
   * Info-level logging (development only)
   * Use for general application flow information
   */
  info = (message: string, context?: LogContext): void => {
    if (!this.shouldLog('info')) return;

    const formatted = this.formatMessage('info', message, {
      ...context,
      sessionId: this.sessionId,
    });
    console.log(formatted);
  };

  /**
   * Warning-level logging (development + production)
   * Use for recoverable errors or unexpected situations
   */
  warn = (message: string, context?: LogContext): void => {
    if (!this.shouldLog('warn')) return;

    const formatted = this.formatMessage('warn', message, {
      ...context,
      sessionId: this.sessionId,
    });
    console.warn(formatted);

    // In production, send to error tracking service
    if (!this.isDevelopment) {
      this.sendToErrorTracking('warn', message, context);
    }
  };

  /**
   * Error-level logging (development + production)
   * Use for errors that need immediate attention
   *
   * Accepts anything thrown — Supabase error objects, strings, plain records — and
   * normalizes them to a real Error before sending to Sentry, so the captured
   * exception has a message, stack, and group-able fingerprint.
   */
  error = (message: string, error?: unknown, context?: LogContext): void => {
    if (!this.shouldLog('error')) return;

    const normalized = error !== undefined ? normalizeError(error) : undefined;

    const errorContext = {
      ...context,
      sessionId: this.sessionId,
      stack: normalized?.stack,
      errorMessage: normalized?.message,
      // Preserve Supabase fields on the context extras too.
      errorCode: normalized?.code,
      errorDetails: normalized?.details,
      errorHint: normalized?.hint,
    };

    const formatted = this.formatMessage('error', message, errorContext);
    console.error(formatted);

    // In production, send to error tracking service
    if (!this.isDevelopment) {
      this.sendToErrorTracking('error', message, errorContext, normalized);
    }
  };

  /**
   * Performance timing utility
   * Measures execution time of code blocks
   */
  time = (label: string): (() => void) => {
    if (!this.shouldLog('debug')) return () => {};

    const start = performance.now();
    this.debug(`⏱️ ${label} - started`);

    return () => {
      const duration = performance.now() - start;
      this.debug(`⏱️ ${label} - completed`, { duration: `${duration.toFixed(2)}ms` });
    };
  };

  /**
   * Game-specific logging utilities
   */
  game = {
    handPlayed: (handName: string, score: number, multiplier: number) => {
      this.debug('Hand played', {
        component: 'GameLogic',
        handName,
        score,
        multiplier,
      });
    },

    sessionStart: (gameMode: string) => {
      this.info('Game session started', {
        component: 'GameSession',
        gameMode,
      });
    },

    sessionEnd: (finalScore: number, handsPlayed: number) => {
      this.info('Game session ended', {
        component: 'GameSession',
        finalScore,
        handsPlayed,
      });
    },

    streakEvent: (event: string, details: Record<string, unknown>) => {
      this.debug(`Streak: ${event}`, {
        component: 'StreakManager',
        ...details,
      });
    },
  };

  /**
   * Database operation logging
   */
  db = {
    query: (table: string, operation: string) => {
      this.debug(`DB: ${operation} on ${table}`, {
        component: 'Database',
        table,
        operation,
      });
    },

    error: (table: string, operation: string, error: unknown) => {
      this.error(`DB Error: ${operation} on ${table}`, error, {
        component: 'Database',
        table,
        operation,
      });
    },

    success: (table: string, operation: string, recordCount?: number) => {
      this.debug(`DB Success: ${operation} on ${table}`, {
        component: 'Database',
        table,
        operation,
        recordCount,
      });
    },
  };

  /**
   * Mobile-specific logging for Play Store debugging
   */
  mobile = {
    /**
     * Log OAuth authentication events
     */
    oauth: {
      start: (provider: string) => {
        this.info('OAuth authentication started', {
          component: 'MobileAuth',
          provider,
          platform: this.getPlatform(),
        });
      },

      success: (provider: string, userId: string) => {
        this.info('OAuth authentication successful', {
          component: 'MobileAuth',
          provider,
          userId,
          platform: this.getPlatform(),
        });
      },

      error: (provider: string, error: Error) => {
        this.error('OAuth authentication failed', error, {
          component: 'MobileAuth',
          provider,
          platform: this.getPlatform(),
          errorCode: this.extractErrorCode(error),
        });
      },

      redirect: (url: string, success: boolean) => {
        this.info('OAuth redirect handled', {
          component: 'MobileAuth',
          redirectUrl: url,
          success,
          platform: this.getPlatform(),
        });
      },
    },

    /**
     * Log game mode transitions
     */
    gameMode: {
      start: (mode: string) => {
        this.info('Game mode started', {
          component: 'MobileGame',
          gameMode: mode,
          platform: this.getPlatform(),
          timestamp: Date.now(),
        });
      },

      crash: (mode: string, error: Error) => {
        this.error('Game mode crashed', error, {
          component: 'MobileGame',
          gameMode: mode,
          platform: this.getPlatform(),
          crashPoint: this.getCrashPoint(),
        });
      },

      success: (mode: string, score: number, duration: number) => {
        this.info('Game mode completed successfully', {
          component: 'MobileGame',
          gameMode: mode,
          finalScore: score,
          duration,
          platform: this.getPlatform(),
        });
      },
    },

    /**
     * Log performance metrics
     */
    performance: {
      memory: () => {
        if (typeof window === 'undefined') return;
        const memory = (window as WindowWithLoggerGlobals).performance?.memory;
        if (memory) {
          this.debug('Memory usage', {
            component: 'MobilePerformance',
            used: Math.round(memory.usedJSHeapSize / 1024 / 1024) + 'MB',
            total: Math.round(memory.totalJSHeapSize / 1024 / 1024) + 'MB',
            limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024) + 'MB',
            platform: this.getPlatform(),
          });
        }
      },

      render: (component: string, duration: number) => {
        if (duration > 100) { // Only log slow renders
          this.warn('Slow render detected', {
            component: 'MobilePerformance',
            renderComponent: component,
            duration: duration + 'ms',
            platform: this.getPlatform(),
          });
        }
      },

      network: (url: string, duration: number, success: boolean) => {
        this.debug('Network request', {
          component: 'MobilePerformance',
          url,
          duration: duration + 'ms',
          success,
          platform: this.getPlatform(),
        });
      },
    },

    /**
     * Log user interactions for debugging
     */
    interaction: {
      button: (buttonName: string, success: boolean) => {
        this.debug('Button interaction', {
          component: 'MobileInteraction',
          button: buttonName,
          success,
          platform: this.getPlatform(),
        });
      },

      gesture: (gestureType: string, success: boolean) => {
        this.debug('Gesture interaction', {
          component: 'MobileInteraction',
          gesture: gestureType,
          success,
          platform: this.getPlatform(),
        });
      },

      navigation: (from: string, to: string) => {
        this.debug('Navigation', {
          component: 'MobileNavigation',
          from,
          to,
          platform: this.getPlatform(),
        });
      },
    },
  };

  /**
   * Extract error code from error message
   */
  private extractErrorCode(error: Error): string | null {
    const message = error.message.toLowerCase();
    if (message.includes('requested path invalid')) return 'INVALID_PATH';
    if (message.includes('network error')) return 'NETWORK_ERROR';
    if (message.includes('timeout')) return 'TIMEOUT';
    if (message.includes('unauthorized')) return 'UNAUTHORIZED';
    return null;
  }

  /**
   * Get crash point for debugging
   */
  private getCrashPoint(): string {
    try {
      const stack = new Error().stack;
      if (stack) {
        const lines = stack.split('\n');
        // Get the second line (first is the error, second is where it was called)
        return lines[2]?.trim() || 'unknown';
      }
    } catch (e) {
      // Ignore errors
    }
    return 'unknown';
  }

  /**
   * Send logs to external error tracking service
   * Enhanced for Play Store production debugging
   */
  private sendToErrorTracking(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: NormalizedError
  ): void {
    // Enhanced context for production debugging
    const enhancedContext = {
      ...context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      platform: this.getPlatform(),
      version: this.getAppVersion(),
      sessionId: this.sessionId,
      userId: this.getUserId(),
      gameState: this.getGameState(),
    };

    // Send to tracking services
    this.sendToSentry(level, message, enhancedContext, error);
    this.sendToAnalytics(level, message, enhancedContext, error);
  }

  /**
   * Send to Sentry for error tracking
   */
  private sendToSentry(
    level: LogLevel,
    message: string,
    context: Record<string, unknown>,
    error?: Error
  ): void {
    try {
      // Check if Sentry is available
      const Sentry = typeof window !== 'undefined'
        ? (window as WindowWithLoggerGlobals).Sentry
        : undefined;
      if (Sentry) {
        if (error) {
          Sentry.captureException(error, {
            level,
            tags: {
              component: context.component,
              action: context.action,
              platform: context.platform,
            },
            extra: context,
          });
        } else {
          Sentry.captureMessage(message, {
            level,
            tags: {
              component: context.component,
              action: context.action,
              platform: context.platform,
            },
            extra: context,
          });
        }
      }
    } catch (e) {
      console.warn('Failed to send to Sentry:', e);
    }
  }

  /**
   * Send to analytics service
   */
  private sendToAnalytics(
    level: LogLevel,
    message: string,
    context: Record<string, unknown>,
    error?: Error
  ): void {
    try {
      if (typeof window === 'undefined') return;
      const win = window as WindowWithLoggerGlobals;

      // Google Analytics 4
      if (win.gtag) {
        win.gtag('event', 'error', {
          error_message: message,
          error_type: error?.name || 'Unknown',
          error_stack: error?.stack,
          component: context.component,
          platform: context.platform,
          severity: level,
        });
      }

      // Firebase Analytics
      const firebase = win.firebase;
      if (firebase?.analytics) {
        firebase.analytics().logEvent('app_error', {
          error_message: message,
          error_type: error?.name || 'Unknown',
          component: context.component,
          platform: context.platform,
          severity: level,
        });
      }
    } catch (e) {
      console.warn('Failed to send to analytics:', e);
    }
  }

  /**
   * Send to custom logging endpoint
   */
  private sendToCustomEndpoint(
    level: LogLevel,
    message: string,
    context: Record<string, unknown>,
    error?: Error
  ): void {
    try {
      // Only send errors and warnings to reduce noise
      if (level === 'error' || level === 'warn') {
        const payload = {
          level,
          message,
          error: error ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          } : null,
          context,
          timestamp: new Date().toISOString(),
        };

        // Send to your custom logging endpoint
        fetch('/api/logs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }).catch(() => {
          // Silently fail to avoid infinite loops
        });
      }
    } catch (e) {
      // Silently fail to avoid infinite loops
    }
  }

  /**
   * Get current platform information
   */
  private getPlatform(): string {
    if (typeof window === 'undefined') return 'unknown';
    
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('android')) return 'android';
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) return 'ios';
    if (userAgent.includes('capacitor')) return 'capacitor';
    return 'web';
  }

  /**
   * Get app version from package.json or environment
   */
  private getAppVersion(): string {
    return import.meta.env.VITE_APP_VERSION || '1.0.0';
  }

  /**
   * Get current user ID if available
   */
  private getUserId(): string | null {
    try {
      // Try to get from localStorage or context
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        return userData.id || userData.user_id || null;
      }
    } catch (e) {
      // Ignore errors
    }
    return null;
  }

  /**
   * Get current game state for debugging
   */
  private getGameState(): Record<string, unknown> {
    try {
      return {
        gameMode: localStorage.getItem('gameMode') || 'unknown',
        isGameActive: localStorage.getItem('isGameActive') === 'true',
        currentScore: localStorage.getItem('currentScore') || '0',
        handsPlayed: localStorage.getItem('handsPlayed') || '0',
      };
    } catch (_e) {
      return { error: 'Failed to get game state' };
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience functions - BIND to preserve 'this' context
export const debug = logger.debug.bind(logger);
export const info = logger.info.bind(logger);
export const warn = logger.warn.bind(logger);
export const error = logger.error.bind(logger);
export const time = logger.time.bind(logger);

/**
 * Capture any value as a proper Error in Sentry/analytics. Use this instead of
 * calling Sentry.captureException directly so Supabase-style objects get
 * normalized consistently.
 */
export function captureError(
  err: unknown,
  message?: string,
  context?: LogContext
): void {
  const normalized = normalizeError(err);
  logger.error(message ?? normalized.message, normalized, context);
}

// Export namespaced functions
export const gameLog = logger.game;
export const dbLog = logger.db;

